import { useState, useEffect } from 'react'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { 
  Search, 
  Filter, 
  Code, 
  Globe, 
  Lock, 
  Unlock,
  Info,
  Tag,
  Clock,
  Server,
  Edit,
  Save,
  X,
  Power,
  PowerOff
} from 'lucide-react'
import { api, serversApi } from '@/lib/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import React from 'react'

interface ServerTool {
  name: string
  description: string
  method: string
  path: string
  server_id: string
  server_name: string
  created_at: string
  requires_auth: boolean
  tags: string[]
  parameters: any[]
  request_body?: any
  responses: any
  schema: any
  enabled: boolean
}

interface ToolsResponse {
  tools: ServerTool[]
  count: number
}

interface ToolDetailsProps {
  tool: ServerTool
  serverId: string
}

function ToolDetails({ tool, serverId }: ToolDetailsProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedDescription, setEditedDescription] = useState(tool.description || '')
  const queryClient = useQueryClient()

  const { data: toolDetails, isLoading: detailsLoading } = useQuery({
    queryKey: ['toolDetails', serverId, tool.name],
    queryFn: async () => await api.get(`/servers/${serverId}/tools/${tool.name}`),
    enabled: !!serverId && !!tool.name,
  })

  const updateDescriptionMutation = useMutation({
    mutationFn: (newDescription: string) => 
      serversApi.updateToolDescription(serverId, tool.name, newDescription),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['toolDetails', serverId, tool.name] })
      queryClient.invalidateQueries({ queryKey: ['serverTools', serverId] })
      setIsEditing(false)
      toast.success('Tool description updated successfully')
    },
    onError: (error: any) => {
      toast.error('Failed to update tool description')
      console.error('Update error:', error)
    }
  })

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'bg-green-100 text-green-800 border-green-200'
      case 'POST': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'PUT': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'DELETE': return 'bg-red-100 text-red-800 border-red-200'
      case 'PATCH': return 'bg-purple-100 text-purple-800 border-purple-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  if (detailsLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    )
  }

  // Use the tool data from props as fallback, and try to get enhanced data from API
  const details = (toolDetails as any)?.tool || tool

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge className={`${getMethodColor(details.method)} font-mono text-xs`}>
            {details.method}
          </Badge>
          <code className="text-sm bg-gray-100 px-2 py-1 rounded">{details.path}</code>
        </div>
        <h3 className="text-lg font-semibold">{details.name}</h3>
        
        {/* Editable Description */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Description</span>
            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsEditing(true)
                  setEditedDescription(details.description || '')
                }}
                className="h-6 px-2"
              >
                <Edit className="h-3 w-3" />
              </Button>
            )}
          </div>
          
          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                placeholder="Enter tool description..."
                className="min-h-[80px]"
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => updateDescriptionMutation.mutate(editedDescription)}
                  disabled={updateDescriptionMutation.isPending}
                  className="h-7"
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsEditing(false)
                    setEditedDescription(details.description || '')
                  }}
                  className="h-7"
                >
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">
              {details.description || 'No description available'}
            </p>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            <span className="text-sm font-medium">Authentication</span>
          </div>
          <Badge variant={details.requires_auth ? "destructive" : "secondary"}>
            {details.requires_auth ? "Required" : "Not Required"}
          </Badge>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            {details.enabled ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
            <span className="text-sm font-medium">Status</span>
          </div>
          <Badge variant={details.enabled ? "default" : "secondary"} 
                 className={details.enabled 
                   ? 'bg-green-100 text-green-800 border-green-200' 
                   : 'bg-gray-100 text-gray-600 border-gray-200'
                 }>
            {details.enabled ? "Enabled" : "Disabled"}
          </Badge>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4" />
            <span className="text-sm font-medium">Tags</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {details.tags && Array.isArray(details.tags) && details.tags.length > 0 ? (
              details.tags.filter((tag: any) => tag && typeof tag === 'string').map((tag: string, index: number) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))
            ) : (
              <span className="text-xs text-muted-foreground">No tags</span>
            )}
          </div>
        </div>
      </div>

      {/* Parameters */}
      {details.parameters && details.parameters.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Parameters</h4>
          <div className="space-y-2">
            {details.parameters.map((param: any, index: number) => (
              <div key={index} className="border rounded p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <code className="text-sm font-medium">{param.name}</code>
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-xs">{param.in}</Badge>
                    {param.required && (
                      <Badge variant="destructive" className="text-xs">Required</Badge>
                    )}
                  </div>
                </div>
                {param.description && (
                  <p className="text-xs text-muted-foreground">{param.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request Body */}
      {details.request_body && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Request Body</h4>
          <div className="border rounded p-3">
            <p className="text-xs text-muted-foreground mb-2">{details.request_body.description}</p>
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto">
              <code>{JSON.stringify(details.request_body, null, 2)}</code>
            </pre>
          </div>
        </div>
      )}

      {/* Usage Statistics */}
      {(toolDetails as any)?.usage && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Usage Statistics</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Total Calls:</span>
              <span className="ml-2 font-medium">{(toolDetails as any).usage?.total_calls || 0}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Success Rate:</span>
              <span className="ml-2 font-medium">{((toolDetails as any).usage?.success_rate * 100)?.toFixed(1) || '0.0'}%</span>
            </div>
            <div>
              <span className="text-muted-foreground">Avg Duration:</span>
              <span className="ml-2 font-medium">{(toolDetails as any).usage?.avg_duration || '0s'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Last Called:</span>
              <span className="ml-2 font-medium">
                {(toolDetails as any).usage?.last_called || 'Never'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface ToolsTabProps {
  serverId: string
  serverName?: string
}

export function ToolsTab({ serverId, serverName }: ToolsTabProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [methodFilter, setMethodFilter] = useState<string>('all')
  const [authFilter, setAuthFilter] = useState<string>('all')
  const [tagFilter, setTagFilter] = useState<string>('all')
  const [enabledFilter, setEnabledFilter] = useState<string>('all')
  const queryClient = useQueryClient()

  const { data: toolsData, isLoading, error, refetch } = useQuery<ToolsResponse>({
    queryKey: ['serverTools', serverId],
    queryFn: async () => await api.get(`/servers/${serverId}/tools`),
    enabled: !!serverId,
  })

  const toggleToolMutation = useMutation({
    mutationFn: async ({ toolName, enabled }: { toolName: string; enabled: boolean }) => {
      if (enabled) {
        return await serversApi.enableTool(serverId, toolName)
      } else {
        return await serversApi.disableTool(serverId, toolName)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serverTools', serverId] })
      toast.success('Tool status updated successfully')
    },
    onError: (error: any) => {
      toast.error('Failed to update tool status')
      console.error('Toggle tool error:', error)
    }
  })

  const tools = Array.isArray(toolsData?.tools) ? toolsData.tools : []
  const toolsCount = toolsData?.count || 0

  // Debug logging
  React.useEffect(() => {
    if (toolsData) {
      console.log('ToolsTab - toolsData:', toolsData)
      console.log('ToolsTab - tools array:', tools)
      console.log('ToolsTab - tools count:', tools.length)
    }
  }, [toolsData, tools])

  // Get unique values for filters - filter out invalid values
  const uniqueMethods = [...new Set(
    tools
      .filter(tool => tool && tool.method && typeof tool.method === 'string' && tool.method.trim().length > 0)
      .map(tool => tool.method.trim())
  )]
  
  const uniqueTags = [...new Set(
    tools
      .filter(tool => tool && tool.tags && Array.isArray(tool.tags) && tool.tags.length > 0)
      .flatMap(tool => tool.tags)
      .filter(tag => tag && typeof tag === 'string' && tag.trim().length > 0)
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0) // Extra check after trimming
  )]

  // Debug logging for filters
  React.useEffect(() => {
    console.log('ToolsTab - uniqueMethods:', uniqueMethods)
    console.log('ToolsTab - uniqueTags:', uniqueTags)
  }, [uniqueMethods, uniqueTags])

  // Filter tools based on search and filters
  const filteredTools = tools.filter(tool => {
    if (!tool) return false
    
    const matchesSearch = !searchQuery || 
      (tool.name && tool.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (tool.description && tool.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (tool.path && tool.path.toLowerCase().includes(searchQuery.toLowerCase()))
    
    const matchesMethod = !methodFilter || methodFilter === 'all' || tool.method === methodFilter
    const matchesAuth = !authFilter || authFilter === 'all' || 
      (authFilter === 'required' && tool.requires_auth) ||
      (authFilter === 'not-required' && !tool.requires_auth)
    
    const matchesTag = !tagFilter || tagFilter === 'all' || (tool.tags && Array.isArray(tool.tags) && tool.tags.includes(tagFilter))
    
    const matchesEnabled = !enabledFilter || enabledFilter === 'all' ||
      (enabledFilter === 'enabled' && tool.enabled) ||
      (enabledFilter === 'disabled' && !tool.enabled)

    return matchesSearch && matchesMethod && matchesAuth && matchesTag && matchesEnabled
  })

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'bg-green-100 text-green-800 border-green-200'
      case 'POST': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'PUT': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'DELETE': return 'bg-red-100 text-red-800 border-red-200'
      case 'PATCH': return 'bg-purple-100 text-purple-800 border-purple-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setMethodFilter('all')
    setAuthFilter('all')
    setTagFilter('all')
    setEnabledFilter('all')
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Available Tools</CardTitle>
          <CardDescription>Loading tools...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="border rounded p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Available Tools</CardTitle>
          <CardDescription>Error loading tools</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              Failed to load tools. Please try again.
            </p>
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with search and filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Available Tools</CardTitle>
              <CardDescription>
                {toolsCount} tool{toolsCount !== 1 ? 's' : ''} generated from the OpenAPI specification
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="flex items-center gap-1">
                <Server className="h-3 w-3" />
                {serverName}
              </Badge>
              <Badge variant="secondary">
                {filteredTools.length} of {toolsCount}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tools by name, description, or path..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              disabled={!searchQuery && methodFilter === 'all' && authFilter === 'all' && tagFilter === 'all' && enabledFilter === 'all'}
            >
              Clear
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <Select value={methodFilter} onValueChange={setMethodFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="GET">GET</SelectItem>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="PUT">PUT</SelectItem>
                <SelectItem value="DELETE">DELETE</SelectItem>
                <SelectItem value="PATCH">PATCH</SelectItem>
              </SelectContent>
            </Select>

            <Select value={authFilter} onValueChange={setAuthFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Authentication" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tools</SelectItem>
                <SelectItem value="required">Auth Required</SelectItem>
                <SelectItem value="not-required">No Auth Required</SelectItem>
              </SelectContent>
            </Select>

            <Select value={enabledFilter} onValueChange={setEnabledFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="enabled">Enabled</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>

            {uniqueTags.length > 0 && uniqueTags.every(tag => tag && typeof tag === 'string') && (
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {uniqueTags
                    .slice(0, 10) // Limit to first 10 tags to prevent overflow
                    .map((tag, index) => (
                      <SelectItem key={`tag-${index}-${tag}`} value={tag}>
                        {tag}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tools List */}
      {filteredTools.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            {toolsCount === 0 ? (
              <>
                <Code className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Tools Available</h3>
                <p className="text-muted-foreground">
                  This server doesn't have any tools yet. Tools are automatically generated from the OpenAPI specification when you create or update a server.
                </p>
              </>
            ) : (
              <>
                <Filter className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Matching Tools</h3>
                <p className="text-muted-foreground">
                  No tools match your current search and filter criteria. Try adjusting your filters.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredTools.map((tool, index) => {
            if (!tool || !tool.name) return null
            
            return (
              <Card key={`tool-${index}-${tool.name}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2 overflow-hidden">
                        <Badge className={`${getMethodColor(tool.method || 'GET')} font-mono text-xs`}>
                          {tool.method || 'GET'}
                        </Badge>
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded truncate max-w-full">{tool.path || '/'}</code>
                        {tool.requires_auth && (
                          <Badge variant="destructive" className="text-xs flex items-center gap-1 whitespace-nowrap">
                            <Lock className="h-3 w-3" />
                            Auth Required
                          </Badge>
                        )}
                        <Badge 
                          variant={tool.enabled ? "default" : "secondary"} 
                          className={`text-xs flex items-center gap-1 whitespace-nowrap ${
                            tool.enabled 
                              ? 'bg-green-100 text-green-800 border-green-200' 
                              : 'bg-gray-100 text-gray-600 border-gray-200'
                          }`}
                        >
                          {tool.enabled ? (
                            <Power className="h-3 w-3" />
                          ) : (
                            <PowerOff className="h-3 w-3" />
                          )}
                          {tool.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      
                      <div className="min-w-0">
                        <h4 className="font-semibold truncate" title={tool.name}>{tool.name}</h4>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {tool.description || 'No description available'}
                        </p>
                      </div>

                      {tool.tags && Array.isArray(tool.tags) && tool.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {tool.tags
                            .filter(tag => tag && typeof tag === 'string')
                            .slice(0, 5) // Limit to 5 tags to prevent overflow
                            .map((tag, tagIndex) => (
                              <Badge key={`tool-tag-${index}-${tagIndex}-${tag}`} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))
                          }
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0 flex items-center gap-2 ml-4">
                      <div className="flex flex-col items-center gap-1">
                        <Switch
                          checked={tool.enabled}
                          onCheckedChange={(enabled) => 
                            toggleToolMutation.mutate({ toolName: tool.name, enabled })
                          }
                          disabled={toggleToolMutation.isPending}
                          className="data-[state=checked]:bg-green-600"
                        />
                        <span className="text-xs text-muted-foreground">
                          {tool.enabled ? 'On' : 'Off'}
                        </span>
                      </div>
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Info className="h-4 w-4 mr-1" />
                            Details
                          </Button>
                        </DialogTrigger>
                      <DialogContent className="max-w-4xl">
                        <DialogHeader>
                          <DialogTitle>Tool Details</DialogTitle>
                          <DialogDescription>
                            Detailed information about the {tool.name} tool
                          </DialogDescription>
                        </DialogHeader>
                        <ToolDetails tool={tool} serverId={serverId} />
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
} 